import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'effector-react';
import spamUsers, {
  clearPrefetchedPage,
  DEFAULT_SPAM_USERS_FILTERS,
  getList,
  isCurrentPrefetchRequest,
  prefetchPage,
  resetFastPrefetch,
  showPrefetchedPage,
} from '@stores/spamUsers/spamUsers';
import { Spin, Pagination, Empty } from 'antd';
import css from './SpamUsersList.module.less';
import { pageSizeOptions } from '@constants/pagination';
import { SpamUserCard } from '@components/SpamUserCard';
import localeWeightSpamUsers, { LOCALE } from '@stores/spamUsers/localeWeightSpamUsers';

interface SpamUsersListProps {}

const SpamUsersList: FunctionComponent<SpamUsersListProps> = () => {
  const {
    list,
    pagination,
    preloaders,
    prefetchedPage,
    prefetchedPageRequest,
    shouldPrefetchNextPageFast,
    setPagination,
    filters,
  } = useStore(spamUsers);
  const locale = useStore(localeWeightSpamUsers);

  const prefetchTimeoutRef = useRef<number | null>(null);
  const prefetchedApplyTimeoutRef = useRef<number | null>(null);
  const [isApplyingPrefetchedPage, setIsApplyingPrefetchedPage] = useState(false);
  const [waitingPrefetchedPage, setWaitingPrefetchedPage] = useState<number | null>(null);

  const totalPages = useMemo(() => {
    if (!pagination.total || !pagination.pageSize) {
      return 0;
    }

    return Math.ceil(pagination.total / pagination.pageSize);
  }, [pagination.total, pagination.pageSize]);

  const nextPage = useMemo(() => {
    const currentPage = pagination.current || 1;
    const calculatedNextPage = currentPage + 1;

    if (!totalPages || calculatedNextPage > totalPages) {
      return null;
    }

    return calculatedNextPage;
  }, [pagination.current, totalPages]);

  const currentCacheKey = useMemo(() => {
    const localeValue = locale === LOCALE.ALL ? undefined : locale;

    return JSON.stringify({
      ...DEFAULT_SPAM_USERS_FILTERS,
      ...filters,
      locale: localeValue,
      pageSize: pagination.pageSize,
    });
  }, [filters, locale, pagination.pageSize]);

  const prefetchDelay = shouldPrefetchNextPageFast ? 300 : 5000;

  useEffect(() => {
    if (prefetchTimeoutRef.current) {
      window.clearTimeout(prefetchTimeoutRef.current);
    }

    if (waitingPrefetchedPage) {
      return undefined;
    }

    if (prefetchedPageRequest?.isLoading) {
      return undefined;
    }

    if (!nextPage) {
      resetFastPrefetch();
      return undefined;
    }

    if (
      prefetchedPage &&
      prefetchedPage.page === nextPage &&
      prefetchedPage.pageSize === pagination.pageSize &&
      prefetchedPage.cacheKey === currentCacheKey &&
      prefetchedPage.items.length > Math.ceil((pagination.pageSize || 0) / 2)
    ) {
      resetFastPrefetch();
      return undefined;
    }

    prefetchTimeoutRef.current = window.setTimeout(() => {
      prefetchPage(nextPage);
    }, prefetchDelay);

    return () => {
      if (prefetchTimeoutRef.current) {
        window.clearTimeout(prefetchTimeoutRef.current);
      }
    };
  }, [nextPage, pagination.pageSize, prefetchedPage, currentCacheKey, prefetchDelay]);

  useEffect(
    () => () => {
      if (prefetchedApplyTimeoutRef.current) {
        window.clearTimeout(prefetchedApplyTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!waitingPrefetchedPage) {
      return;
    }

    if (
      prefetchedPage &&
      prefetchedPage.page === waitingPrefetchedPage &&
      prefetchedPage.pageSize === pagination.pageSize &&
      prefetchedPage.cacheKey === currentCacheKey
    ) {
      showPrefetchedPage(prefetchedPage.items);
      clearPrefetchedPage();
      setWaitingPrefetchedPage(null);

      if (prefetchedApplyTimeoutRef.current) {
        window.clearTimeout(prefetchedApplyTimeoutRef.current);
      }

      prefetchedApplyTimeoutRef.current = window.setTimeout(() => {
        setIsApplyingPrefetchedPage(false);
      }, 200);

      return;
    }

    if (
      prefetchedPageRequest &&
      !prefetchedPageRequest.isLoading &&
      prefetchedPageRequest.page === waitingPrefetchedPage &&
      prefetchedPageRequest.pageSize === pagination.pageSize &&
      prefetchedPageRequest.cacheKey === currentCacheKey &&
      (!prefetchedPage || prefetchedPage.page !== waitingPrefetchedPage)
    ) {
      setWaitingPrefetchedPage(null);
      setIsApplyingPrefetchedPage(false);
      clearPrefetchedPage();
      getList();
    }
  }, [
    nextPage,
    pagination.pageSize,
    prefetchedPage,
    prefetchedPageRequest,
    currentCacheKey,
    prefetchDelay,
    waitingPrefetchedPage,
  ]);

  const paginationChange = (current: number, pageSize?: number) => {
    const nextPageSize = pageSize || pagination.pageSize;

    if (
      prefetchedPage &&
      prefetchedPage.page === current &&
      prefetchedPage.pageSize === nextPageSize &&
      prefetchedPage.cacheKey === currentCacheKey
    ) {
      if (prefetchedApplyTimeoutRef.current) {
        window.clearTimeout(prefetchedApplyTimeoutRef.current);
      }

      setIsApplyingPrefetchedPage(true);
      setWaitingPrefetchedPage(null);

      setPagination({
        ...pagination,
        current,
        pageSize: nextPageSize,
        total: prefetchedPage.total,
      });
      showPrefetchedPage(prefetchedPage.items);
      clearPrefetchedPage();

      prefetchedApplyTimeoutRef.current = window.setTimeout(() => {
        setIsApplyingPrefetchedPage(false);
      }, 200);

      return;
    }

    if (isCurrentPrefetchRequest(prefetchedPageRequest, current, nextPageSize, currentCacheKey)) {
      if (prefetchedApplyTimeoutRef.current) {
        window.clearTimeout(prefetchedApplyTimeoutRef.current);
      }

      setIsApplyingPrefetchedPage(true);
      setWaitingPrefetchedPage(current);
      setPagination({
        ...pagination,
        current,
        pageSize: nextPageSize,
      });

      return;
    }

    setIsApplyingPrefetchedPage(false);
    clearPrefetchedPage();
    setPagination({ ...pagination, current, pageSize: nextPageSize });
    getList();
  };

  if (!list.length && !preloaders.getList) {
    return <Empty />;
  }

  return (
    <div>
      <div className={css.paginationContainer}>
        <Pagination
          {...pagination}
          showQuickJumper
          onChange={paginationChange}
          showSizeChanger
          pageSizeOptions={pageSizeOptions}
        />
      </div>
      <Spin spinning={!!preloaders.getList || isApplyingPrefetchedPage}>
        <div className={css.grid}>
          {list.map((user) => (
            <SpamUserCard
              key={user.id}
              user={user}
            />
          ))}
        </div>
      </Spin>
      <div className={css.paginationContainer}>
        <Pagination
          {...pagination}
          showQuickJumper
          onChange={paginationChange}
          showSizeChanger
          pageSizeOptions={pageSizeOptions}
        />
      </div>
    </div>
  );
};

export default SpamUsersList;
